// Types shared between the Worker API and the React app.

export interface Category {
  id: string
  name: string
  slug: string
  blurb: string
  emoji: string
  color: string
  /**
   * Which physical container this category is packed into, and the label
   * shown above its group on the board. Categories sharing a value are
   * packed together; a different value starts a new container.
   */
  container: string
  /**
   * Categories sharing a non-empty value accept each other's foods, because
   * they differ by container rather than by kind: Side and Snack are the same
   * snack in two boxes. Empty means this category takes only its own.
   */
  interchangeableGroup: string
  sortOrder: number
}

export interface Item {
  id: string
  categoryId: string
  name: string
  notes: string
  emoji: string
  color: string
  imageKey: string | null
  archived: boolean
  sortOrder: number
  updatedAt: string
  updatedBy: string
  /**
   * What a parent says this food contains. An empty list means nobody has
   * said so yet -- never that the food is free of anything.
   */
  allergens: ItemAllergen[]
  /** When a human last read this food's label, or null if nobody has. */
  allergensCheckedAt: string | null
  allergensCheckedBy: string
}

/* ---- Allergens ---------------------------------------------------------- */

/**
 * The allergens worth a checkbox: the nine most common, plus gluten, which is
 * not a wheat allergy and is what a coeliac parent looks for. Anything else is
 * an 'other' row carrying the parent's own wording.
 */
export const ALLERGENS = [
  { slug: 'milk', label: 'Milk' },
  { slug: 'egg', label: 'Egg' },
  { slug: 'peanut', label: 'Peanut' },
  { slug: 'treenut', label: 'Tree nuts' },
  { slug: 'soy', label: 'Soy' },
  { slug: 'wheat', label: 'Wheat' },
  { slug: 'gluten', label: 'Gluten' },
  { slug: 'fish', label: 'Fish' },
  { slug: 'shellfish', label: 'Shellfish' },
  { slug: 'sesame', label: 'Sesame' },
] as const

export type KnownAllergen = (typeof ALLERGENS)[number]['slug']
export type AllergenSlug = KnownAllergen | IngredientSlug | DietSlug | 'other'

/**
 * Things a food can contain that are not allergies.
 *
 * Nobody ticks "meat" as an allergy, so these get no checkbox of their own in
 * the allergy list. They exist because a dietary restriction needs something
 * on the food to match against: "vegetarian" is not a property of a child, it
 * is a set of ingredients they do not eat.
 */
export const INGREDIENTS = [
  { slug: 'meat', label: 'Meat' },
  { slug: 'pork', label: 'Pork' },
  { slug: 'gelatin', label: 'Gelatin' },
  { slug: 'honey', label: 'Honey' },
] as const

export type IngredientSlug = (typeof INGREDIENTS)[number]['slug']

/** Every slug a *food* may be tagged with. Allergies, ingredients, or free text. */
export const TAG_SLUGS: readonly string[] = [
  ...ALLERGENS.map((a) => a.slug),
  ...INGREDIENTS.map((i) => i.slug),
  'other',
]

/**
 * Kept for the allergy tick-list, which is deliberately narrower than what a
 * food can carry: TAG_SLUGS is the set for tagging foods, this is the set for
 * asking a parent what their child is allergic to.
 */
export const ALLERGEN_SLUGS: readonly string[] = [...ALLERGENS.map((a) => a.slug), 'other']

/**
 * Restrictions that are not allergies.
 *
 * Each one is a shorthand for a set of ingredients, which is the only way the
 * app can act on it: a food is never tagged "vegetarian", it is tagged "meat"
 * and a vegetarian is someone who does not eat that.
 *
 * Halal and kosher are deliberately absent. Both need more than an ingredient
 * list -- certification, how an animal was slaughtered, keeping meat and dairy
 * apart -- and a chip that quietly checked for pork and called it halal would
 * be the app claiming to know something it does not. They belong in the
 * free-text row, in the family's own words.
 */
export const DIETS = [
  { slug: 'vegetarian', label: 'Vegetarian', excludes: ['meat', 'pork', 'fish', 'shellfish', 'gelatin'] },
  { slug: 'vegan', label: 'Vegan', excludes: ['meat', 'pork', 'fish', 'shellfish', 'gelatin', 'milk', 'egg', 'honey'] },
  { slug: 'pescatarian', label: 'Pescatarian', excludes: ['meat', 'pork', 'gelatin'] },
  { slug: 'nopork', label: 'No pork', excludes: ['pork'] },
] as const

export type DietSlug = (typeof DIETS)[number]['slug']
export const DIET_SLUGS: readonly string[] = DIETS.map((d) => d.slug)

/**
 * 'severe' is anaphylaxis, and earns a red warning. 'avoid' is an intolerance
 * or a preference, and earns a quiet note. 'rule' is what a school row
 * carries: a rule has no severity, it simply holds.
 */
export type AllergenSeverity = 'severe' | 'avoid' | 'rule'

/**
 * Something the child cannot eat ('child'), or something the school does not
 * allow ('school'). The distinction matters: a nut-free classroom bans peanut
 * butter for a child who tolerates peanuts perfectly well.
 */
export interface ProfileAllergen {
  id: string
  /**
   * 'child' cannot eat it, 'school' does not allow it, 'diet' is a restriction
   * the family keeps. The three read differently and are worded differently,
   * so they are not collapsed into one.
   */
  kind: 'child' | 'school' | 'diet'
  slug: AllergenSlug
  /** The parent's own wording, used when slug is 'other'. */
  label: string
  severity: AllergenSeverity
  notes: string
}

/** One tag on a food. */
export interface ItemAllergen {
  slug: AllergenSlug
  label: string
}

/** What to call an allergen in the interface. */
export const allergenName = (a: { slug: AllergenSlug; label: string }): string => {
  if (a.slug === 'other') return a.label
  return (
    ALLERGENS.find((k) => k.slug === a.slug)?.label ??
    INGREDIENTS.find((k) => k.slug === a.slug)?.label ??
    DIETS.find((k) => k.slug === a.slug)?.label ??
    a.slug
  )
}

/**
 * Which diets a set of food tags rules the food out for.
 *
 * The tags stay facts about the food -- "contains meat", never "not
 * vegetarian" -- because a fact survives a new diet being added later and a
 * verdict does not. But a parent ticking boxes is owed the verdict, and
 * cannot be expected to hold four excludes lists in their head: half of what
 * makes a food not vegan (milk, egg, fish, shellfish) is filed under
 * allergens, which is where this was easiest to get wrong.
 */
export function dietsRuledOutBy(tags: { slug: string }[]): readonly string[] {
  const has = new Set(tags.map((t) => t.slug))
  return DIETS.filter((d) => d.excludes.some((x) => has.has(x))).map((d) => d.label)
}

/** The ingredients a restriction rules out, or none if it is not a diet. */
export const dietExcludes = (slug: string): readonly AllergenSlug[] =>
  DIETS.find((d) => d.slug === slug)?.excludes ?? []

/** Two tags mean the same thing when the slug matches, and the wording too for 'other'. */
const sameAllergen = (a: { slug: string; label: string }, b: { slug: string; label: string }) =>
  a.slug === b.slug &&
  (a.slug !== 'other' || a.label.trim().toLowerCase() === b.label.trim().toLowerCase())

/**
 * Which of a child's allergens and school rules this food runs into.
 *
 * Only what the parent has tagged can match, so a clean result means "nothing
 * we were told about", never "safe". Every caller has to say so.
 */
export function conflictsFor(item: { allergens: ItemAllergen[] }, against: ProfileAllergen[]): ProfileAllergen[] {
  return against.filter((rule) => {
    /*
     * A diet is not a tag on a food, it is shorthand for a set of them. A
     * vegetarian row matches anything carrying meat, pork, fish, shellfish or
     * gelatin; the food itself never says "vegetarian".
     */
    if (rule.kind === 'diet') {
      const out = dietExcludes(rule.slug)
      return item.allergens.some((tag) => out.includes(tag.slug))
    }
    return item.allergens.some((tag) => sameAllergen(tag, rule))
  })
}

/**
 * How loudly a set of matched rows should speak.
 *
 * 'severe' outranks everything: it is the one that ends in an ambulance.
 * 'rule' comes next, because a school rule or a diet binds even when the food
 * is harmless to the child. 'avoid' is an intolerance or a preference and
 * stays quiet, because a red warning on every mild dislike teaches people to
 * ignore red warnings.
 *
 * Shared rather than client-side because the server now needs it too: copying
 * a lunch to a sibling has to stop on the same conflicts the board stops on,
 * and two copies of that rule would drift.
 */
export type ConflictLevel = 'severe' | 'rule' | 'avoid'

export const conflictLevel = (rows: ProfileAllergen[]): ConflictLevel =>
  rows.some((r) => r.kind === 'child' && r.severity === 'severe')
    ? 'severe'
    : rows.some((r) => r.kind === 'school' || r.kind === 'diet')
      ? 'rule'
      : 'avoid'

/**
 * Whether a conflict should stop and ask rather than just carry a flag.
 *
 * Severe allergies and binding rules do; an intolerance does not. A dialog in
 * front of every mildly disliked food is a dialog people learn to dismiss
 * without reading, which costs exactly the attention the severe case needs.
 */
export const worthStopping = (rows: ProfileAllergen[]): boolean =>
  rows.length > 0 && conflictLevel(rows) !== 'avoid'

/**
 * One thing to check before a lunch lands on a sibling's board: which day,
 * which food, and what about that child it runs into.
 */
export interface SiblingClash {
  dayOfWeek: number
  itemId: string
  name: string
  /** What matched, in the family's own words: "Peanut", "Milk". */
  reasons: string[]
  /** True when this one should stop and ask rather than just carry a flag. */
  stopping: boolean
}

export interface LunchItem {
  /** Row id of the placement, not the item id. Unique per slot. */
  id: string
  itemId: string
  categoryId: string
  sortOrder: number
}

/**
 * How a given weekday is handled.
 *  - `packing`: a normal packed lunch
 *  - `pizza`:   school pizza day (roughly monthly) -- still allows items,
 *               since "BYO pizza" days exist
 *  - `skip`:    nothing to pack at all: day off, vacation, sick, field trip
 */
export type DayStatus = 'packing' | 'pizza' | 'skip'

export const DAY_STATUSES: { value: DayStatus; label: string; emoji: string }[] = [
  { value: 'packing', label: 'Packing lunch', emoji: '🥪' },
  { value: 'pizza', label: 'Pizza day', emoji: '🍕' },
  { value: 'skip', label: 'Skip - no lunch needed', emoji: '🚫' },
]

export interface Lunch {
  id: string
  /** 1 = Monday .. 5 = Friday */
  dayOfWeek: number
  status: DayStatus
  /** Optional detail, e.g. "Vacation" or "Field trip". */
  label: string
  items: LunchItem[]
}

/**
 * A note on a week, or on one day within it. A thread rather than a single
 * field: one parent explains the plan, the other replies, and someone records
 * how it actually went.
 */
export interface Note {
  id: string
  /** null when the note is about the whole week. */
  dayOfWeek: number | null
  body: string
  author: string
  createdAt: string
  updatedAt: string
}

/**
 * A saved combination of items -- a whole lunch rather than one food. Applying
 * one fills a day's slots in a single tap.
 *
 * Just an ordered list of item ids: a meal is a template over the live pantry,
 * so categories are resolved from the items themselves and can never go stale
 * against a re-filed item.
 */
export interface Meal {
  id: string
  name: string
  /** Why this combination works, for the other parent reading the plan. */
  notes: string
  emoji: string
  /** Pinned to the top of the picker. */
  favorite: boolean
  /** How many times this meal has been applied to a day. */
  useCount: number
  lastUsedAt: string | null
  itemIds: string[]
  updatedAt: string
  updatedBy: string
}

/** One turn of a saved assistant conversation. */
export interface AssistantMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  /** Who asked. Answers carry no author. */
  author: string
  createdAt: string
}

/**
 * A change the assistant wants to make, already resolved against real data.
 *
 * `summary` is the whole of what a person sees -- the ids exist so Apply has
 * something exact to act on, and are re-checked server-side either way.
 */
export interface ProposedChange {
  kind: string
  summary: string
  day?: number | null
  items?: string[]
  itemIds?: string[]
  mode?: 'replace' | 'add'
  status?: string
  label?: string
  meal?: string
  mealId?: string
  body?: string
  name?: string
  category?: string
  categoryId?: string
  emoji?: string
}

export interface Proposal {
  weekId: string | null
  actions: ProposedChange[]
  /** What it asked for that could not be done, in words worth showing. */
  problems: string[]
}

/**
 * A saved conversation. Shared between both parents, like everything else
 * here -- "what did it say about this week?" is worth one of us being able to
 * ask the other.
 */
export interface AssistantThread {
  id: string
  /** Taken from the opening question. */
  title: string
  weekId: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  messageCount: number
}

export type ShoppingState = 'bought' | 'have'

/**
 * Where a week is in the handoff between the two of us.
 *  - `planning`:  being built (the default)
 *  - `in_review`: sent to the other parent to look over
 *  - `approved`:  they have signed off
 * Editing a week never changes this; only the review buttons do.
 */
export type ReviewState = 'planning' | 'in_review' | 'approved'

/** Someone who can be emailed, and what they have agreed to hear about. */
/**
 * Whether a contact can open the app:
 *  - `member`:  an account with this address belongs to the family
 *  - `invited`: an invitation is out and has not expired
 *  - `none`:    emailed only; Settings offers to invite them
 */
export type PersonAccess = 'member' | 'invited' | 'none'

export interface Person {
  id: string
  /** Matches the name picked at sign-in; that is how a session finds itself. */
  name: string
  email: string
  /**
   * Free text, and often empty. It is on the printable allergy card, which is
   * the only place it is used: a school needs a number it can ring, and the
   * card used to print a blank line for one to be written on by hand.
   */
  phone: string
  notifyReminder: boolean
  notifyReview: boolean
  access: PersonAccess
}

/** When the "next week isn't planned yet" nudge goes out. */
export interface ReminderSettings {
  enabled: boolean
  /** 0 = Sunday .. 6 = Saturday, in `timezone`. */
  weekday: number
  /** Local hour, 0-23. */
  hour: number
  /** IANA zone, e.g. "America/Denver". */
  timezone: string
}

/** One attempt to send an email, newest first. Shown in settings. */
export interface EmailLogEntry {
  id: string
  kind: string
  recipient: string
  subject: string
  status: 'sending' | 'sent' | 'failed'
  detail: string
  createdAt: string
}

export interface Week {
  id: string
  /** ISO date of the Monday that starts the week. */
  startDate: string
  updatedAt: string
  updatedBy: string
  lunches: Lunch[]
  /** Week-level notes and every day note, oldest first. */
  notes: Note[]
  reviewState: ReviewState
  reviewRequestedBy: string
  reviewRequestedAt: string | null
  reviewedBy: string
  reviewedAt: string | null
}

export interface WeekSummary {
  id: string
  startDate: string
  updatedAt: string
  updatedBy: string
  /** How many notes the week carries, across the week itself and its days. */
  noteCount: number
  /** How many days in the week have at least one item packed. */
  packedDays: number
  /** How many days are marked skip or pizza (nothing to shop for). */
  offDays: number
  /** Total item placements across the week. */
  itemCount: number
  reviewState: ReviewState
}

/**
 * Where a family stands with the person who runs the service.
 *  - `pending`:  signed up on its own; everything works except the assistant
 *                and photo uploads, which wait for approval
 *  - `active`:   approved, or older than sign-up
 *  - `declined`: refused; its sessions get a message, not the app
 */
export type FamilyStatus = 'pending' | 'active' | 'declined'

export interface Session {
  userId: string
  /** Shown as who packed and who edited. */
  name: string
  email: string
  /**
   * Which family's data this session may see. Null only for an account that
   * has not joined or created a family yet; every route that touches data
   * refuses such a session with `needs_family`.
   */
  familyId: string | null
  familyName: string | null
  familyStatus: FamilyStatus | null
  /** Granted by ADMIN_EMAILS. Approves families, sees the admin page. */
  isAdmin: boolean
}

/** What the sign-up screens need before anyone is signed in. */
export interface SignupConfig {
  /** Cloudflare Turnstile site key; blank means the widget is not shown. */
  turnstileSiteKey: string
}

/** One food in the starter pantry a new family reviews. */
export interface StarterItem {
  /** The starter row, so the seeded item can say where it came from. */
  id: string
  categoryId: string
  name: string
  emoji: string
  /**
   * A hint under the name: the brands a generic food goes by, or how it
   * travels. Carried onto the seeded item so the family keeps it.
   */
  notes: string
  /**
   * What this food usually contains. A guess at the common recipe, carried so
   * onboarding can warn before a family builds a pantry around it. Never a
   * reading of any actual label.
   */
  allergens: KnownAllergen[]
}

/** One row of a new family's pantry: a starter food kept, or one typed in. */
export interface NewFamilyItem {
  categoryId: string
  name: string
  emoji: string
  /** The starter row this was kept from; null or absent for one typed in. */
  starterId?: string | null
}

/**
 * "Ada", "Ada and Bo", "Ada, Bo and Cass".
 *
 * Used wherever a sentence names a family's children -- the admin feed, the
 * sign-up email, the reminder. Serial comma left off deliberately: this is
 * running prose in a short line, not a list.
 */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** One child, as onboarding collects them. */
export interface NewFamilyChild {
  name: string
  /** This child's allergies, diet and school rules. May be empty. */
  allergens: NewProfileAllergen[]
}

/** What a new family is asked for, after the account exists and is verified. */
export interface NewFamily {
  familyName: string
  /** The children, eldest first. At least one, and the first is the default. */
  children: NewFamilyChild[]
  /** The other parent, invited by email. Optional: one parent is a family. */
  partner: { name: string; email: string } | null
  /** The pantry, which belongs to the family and is shared by every child. */
  items: NewFamilyItem[]
  reminder: ReminderSettings
  /**
   * What onboarding sent before it could ask for more than one child.
   *
   * A parent part-way through onboarding when a deploy lands still has the
   * old bundle in their tab, and submitting it is the last thing they do.
   * These are folded into `children` on arrival, so that submit still works.
   */
  childName?: string
  allergens?: NewProfileAllergen[]
}

/** An allergen row as onboarding sends it, before it has an id. */
export interface NewProfileAllergen {
  kind: 'child' | 'school' | 'diet'
  slug: AllergenSlug
  label: string
  severity: AllergenSeverity
}

/**
 * The same row while it is still being edited, where severity may be
 * unanswered.
 *
 * There is no safe default for how serious an allergy is. Defaulting to
 * 'avoid' lets an anaphylactic child be recorded as a preference by someone
 * who did not notice the second tap, which fails silently. Defaulting to
 * 'severe' cries wolf on every mild intolerance, which teaches people to
 * ignore the red. So the editor asks, and will not submit until it is told.
 */
export interface DraftAllergen {
  kind: 'child' | 'school' | 'diet'
  slug: AllergenSlug
  label: string
  severity: AllergenSeverity | null
}

/** Child rows still waiting on an answer. Nothing can be saved while any remain. */
export const unansweredAllergens = (rows: DraftAllergen[]): DraftAllergen[] =>
  rows.filter((r) => r.kind === 'child' && r.severity === null)

/** A finished draft as the API wants it. Call only when nothing is unanswered. */
export const asNewAllergens = (rows: DraftAllergen[]): NewProfileAllergen[] =>
  rows.flatMap((r) => (r.severity === null ? [] : [{ ...r, severity: r.severity }]))

/** An invitation as shown to the person holding the link, before they sign in. */
export interface InvitationInfo {
  id: string
  familyId: string
  familyName: string
  inviterName: string
  email: string
  status: 'pending' | 'accepted' | 'rejected' | 'canceled' | 'expired'
}

export type FeedbackCategory = 'idea' | 'bug' | 'confusing' | 'love' | ''

export const FEEDBACK_CATEGORIES: { value: FeedbackCategory; label: string; emoji: string }[] = [
  { value: 'idea', label: 'Idea', emoji: '💡' },
  { value: 'bug', label: 'Something broke', emoji: '🐛' },
  { value: 'confusing', label: 'Confusing', emoji: '🤔' },
  { value: 'love', label: 'Love it', emoji: '❤️' },
]

/** One submission, as the admin page lists it. */
export interface Feedback {
  id: string
  familyId: string
  familyName: string
  userName: string
  userEmail: string
  category: FeedbackCategory
  message: string
  page: string
  weekStart: string
  viewport: string
  userAgent: string
  /** How many screenshots; each is at /api/admin/feedback/:id/images/:n. */
  imageCount: number
  status: 'new' | 'done'
  createdAt: string
}

/** One starter food, as the admin's Pantry page lists it. */
export interface StarterAdminItem {
  id: string
  categoryId: string
  name: string
  emoji: string
  retired: boolean
  /** Who promoted it; '' for the original list. */
  addedBy: string
  createdAt: string
  /** Families seeded while it existed (declined ones excluded). */
  seededTo: number
  /** Of those, how many still have it in the pantry. */
  keptBy: number
}

/** A food families added themselves, folded across families by name. */
export interface PantryAddition {
  name: string
  categoryId: string
  emoji: string
  /** Distinct families that added it. */
  count: number
  families: { id: string; name: string }[]
  latestAt: string
  /**
   * Live starter foods sharing a significant word with this one, most words
   * in common first. A hint, not a verdict: the admin decides whether
   * "Carrots" is really the "Carrot Sticks" we already ship.
   */
  similar: string[]
}

export interface AdminPantry {
  starter: StarterAdminItem[]
  additions: PantryAddition[]
  /** Families counted (everyone but declined). */
  families: number
}

/** One family's pantry against the starter it was seeded from. */
export interface FamilyPantryDiff {
  familyId: string
  familyName: string
  childName: string
  kept: number
  added: { id: string; categoryId: string; name: string; emoji: string; createdAt: string }[]
  removed: { id: string; categoryId: string; name: string; emoji: string }[]
  edited: { starterId: string; categoryId: string; name: string; starterName: string }[]
}

/**
 * A family's weeks, as the admin sees them: the board they actually built.
 *
 * The counts on the admin page say whether a family planned; this says what
 * they planned, which is the only way to tell a week that works from a week
 * that was abandoned after one food.
 *
 * Note bodies are deliberately not here. A note is one parent talking to the
 * other about their own child, and reading those is not what "see how people
 * use it" needs: the count alone says whether the feature is used. Promote
 * them to real content only on a decision to, not by accident.
 */
export interface AdminWeekBoard {
  id: string
  /** ISO date of the Monday that starts the week. */
  startDate: string
  updatedAt: string
  updatedBy: string
  reviewState: ReviewState
  noteCount: number
  days: {
    dayOfWeek: number
    status: DayStatus
    label: string
    items: { id: string; categoryId: string; name: string; emoji: string }[]
  }[]
}

export interface AdminFamilyWeeks {
  familyId: string
  familyName: string
  children: { id: string; name: string; weeks: AdminWeekBoard[] }[]
  /** Weeks held back by the per-child cap, so the page can say so. */
  truncated: number
}

/** One row of the admin page. */
export interface AdminFamily {
  id: string
  name: string
  status: FamilyStatus
  createdAt: string
  approvedAt: string | null
  approvedBy: string
  childName: string
  members: { name: string; email: string; role: string }[]
  itemCount: number
  weekCount: number
  /**
   * Every food this family has ever placed on a board. One is enough to say
   * they got past looking at it.
   */
  placementCount: number
  /**
   * Weeks with no weekday left unplanned and at least one day carrying food.
   * A day marked pizza or skip counts as planned -- someone decided it -- but
   * a week of nothing but skipped days is a vacation, not an activation.
   */
  fullWeekCount: number
}

/**
 * The four steps a family takes, in order, from signing up to using this.
 *
 * Read off the tables rather than the events feed, so it is right for a
 * family that planned its first week before the feed existed. The feed says
 * when; this says whether.
 */
export interface ActivationStep {
  key: 'onboarded' | 'approved' | 'placed' | 'full_week'
  label: string
  done: boolean
  /** Shown under the label when there is something to say. */
  detail: string
}

export function activationOf(f: AdminFamily): ActivationStep[] {
  return [
    { key: 'onboarded', label: 'Finished onboarding', done: true, detail: `${f.itemCount} foods in the pantry` },
    {
      key: 'approved',
      label: 'Approved',
      done: f.status === 'active',
      detail: f.status === 'declined' ? 'Declined' : f.status === 'pending' ? 'Waiting on you' : '',
    },
    {
      key: 'placed',
      label: 'Placed their first food',
      done: f.placementCount > 0,
      detail: f.placementCount > 0 ? `${f.placementCount} placed in all` : 'Never opened the board',
    },
    {
      key: 'full_week',
      label: 'Planned a whole week',
      done: f.fullWeekCount > 0,
      detail:
        f.fullWeekCount > 0
          ? `${f.fullWeekCount} week${f.fullWeekCount === 1 ? '' : 's'} with every day planned`
          : f.weekCount > 0
            ? `${f.weekCount} week${f.weekCount === 1 ? '' : 's'} started, none finished`
            : 'No weeks yet',
    },
  ]
}

/** How far a family got: the last step it completed, 0 to 4. */
export const activationReach = (f: AdminFamily): number => {
  const steps = activationOf(f)
  const stalled = steps.findIndex((s) => !s.done)
  return stalled === -1 ? steps.length : stalled
}

/** One child in a family, as the switcher and Settings show them. */
export interface Child {
  id: string
  name: string
  /** Out of the switcher, but their weeks and allergens are all still there. */
  archived: boolean
}

export interface Bootstrap {
  session: Session
  profileId: string
  profileName: string
  categories: Category[]
  items: Item[]
  /** What the child cannot eat, and what the school does not allow. */
  allergens: ProfileAllergen[]
  meals: Meal[]
  people: Person[]
  reminder: ReminderSettings
  /**
   * Whether the Worker actually holds a Resend key. The settings UI says so
   * plainly rather than letting someone tick switches that quietly do nothing.
   */
  emailConfigured: boolean
  /** Whether the Worker holds an OpenAI key, so the UI can offer or explain. */
  aiConfigured: boolean
  /**
   * Whether this family can use it: the key is set and the family is
   * approved. Pending families get the plain paths, and the UI says so
   * rather than promising a sort that will not come.
   */
  aiAvailable: boolean
}

/** One line of the derived shopping list for a week. */
export interface ShoppingLine {
  itemId: string
  name: string
  emoji: string
  categoryId: string
  imageKey: string | null
  /** Number of lunches across the week that use this item. */
  count: number
  /** Day numbers (1-5) the item is needed for. */
  days: number[]
  /**
   * Which children it is for and how many lunches each, in family order.
   * The list is one shop for the whole family, and "×6" means something
   * different when it is one child every day or three children twice.
   */
  kids: { profileId: string; name: string; count: number }[]
  /** Shared across the family; null means still to buy. */
  state: ShoppingState | null
}

/** One family's shopping list for one Monday, every child's lunches folded in. */
export interface ShoppingList {
  lines: ShoppingLine[]
  extras: ShoppingExtra[]
  extrasLastWeek: number
  /** Allergens and school rules by child id, for the children on the list. */
  allergens: Record<string, ProfileAllergen[]>
}

/**
 * Something to buy that no lunch asked for: milk, kitchen roll, the birthday
 * card. Written by hand and kept beside the derived list so the shopping page
 * is the whole shop rather than a subset of it.
 *
 * It is never a pantry item. An extra has no id in `items`, so it cannot be
 * packed, picked, flagged for an allergy or carried into next week's derived
 * list -- which is the point. Kitchen roll is not Monday's main.
 */
export interface ShoppingExtra {
  id: string
  name: string
  /** Shared across both parents, exactly as a food line is. Null means still to buy. */
  state: ShoppingState | null
}

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const

/* ---- Admin activity feed ------------------------------------------------ */

/**
 * One business event. Not a log line: each is something a person did that an
 * admin might later want to ask about, written as a sentence at the call site.
 */
export interface ActivityEvent {
  id: string
  /** UTC, as SQLite writes it. */
  at: string
  kind: string
  familyId: string | null
  /** The family's name now, or what it was called before it was deleted. */
  familyName: string
  /** The family is gone, so the name above is historical. */
  deleted: boolean
  actor: string
  summary: string
  detail: string
}

/* ---- Broadcasts --------------------------------------------------------- */

/** Which account holders a broadcast went to. Recorded with the send. */
export interface BroadcastAudience {
  statuses: ('active' | 'pending')[]
  /** ISO date. Families created before it were left out. */
  since?: string
}

export interface BroadcastCounts {
  total: number
  sent: number
  failed: number
  /** Unsubscribed between being queued and being sent. Not a failure. */
  skipped: number
  queued: number
}

export interface BroadcastSummary {
  id: string
  subject: string
  /** The source as typed, so a follow-up can start from it. */
  markdown: string
  audience: BroadcastAudience
  status: 'sending' | 'sent' | 'failed'
  createdBy: string
  createdAt: string
  finishedAt: string | null
  counts: BroadcastCounts
}

/** One address on one broadcast, for the detail view. */
export interface BroadcastRecipientRow {
  name: string
  email: string
  status: 'queued' | 'sending' | 'sent' | 'failed' | 'skipped'
  detail: string
  sentAt: string | null
}

/** The glyph each kind shows in the feed, so a scan reads without words. */
export const EVENT_ICONS: Record<string, string> = {
  signed_up: '👋',
  confirmed: '✅',
  family_created: '🏠',
  child_added: '🧒',
  approved: '👍',
  declined: '🚫',
  invited: '✉️',
  first_week: '🍱',
  full_week: '🗓',
  feedback: '💬',
  email_failed: '⚠️',
  broadcast_sent: '📣',
  unsubscribed: '🔕',
}
